import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Link, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';

import { FMS_READ_SCOPE } from '#/api/fmsMgmt/fmsReadScope';
import { fetchVoucherDetail, voucherAttachmentsApi } from '#/api/fmsMgmt/voucher';
import { ATTACHMENT_POLICY_STANDARD } from '#/api/platform/attachments';
import { PERM } from '#/config/permissions';
import { ROUTES } from '#/config/routes';
import { cn } from '#/lib/utils';
import { DocumentAttachmentsCard } from '#/modules/commonComponents/attachments';
import { Button } from '#/modules/commonComponents/button';
import { useCan } from '#/modules/infra/auth/useCan';
import PageShell from '#/modules/infra/layout/PageShell';
import { voucherDetailQueryKey } from '#/utils/queryKeys';
import { toast } from '#/utils/toast';

import VoucherCommandBar from '../components/VoucherCommandBar';
import VoucherMainInfoCard from '../components/VoucherMainInfoCard';
import { getVoucherApiErrorMessage } from '../helper/apiError';
import { canManageVoucherAttachments } from '../helper/voucherStatus';

const BACK_TO = {
  personal: ROUTES.personalCenterVouchersPanel,
  finance: ROUTES.fmsVoucher,
};

/**
 * 憑證詳情。personal＝個人中心；finance＝財務管理（會計審核）。
 */
const VoucherDetailPage = ({ mode = 'personal' }) => {
  const { id } = useParams();
  const canUpdate = useCan(PERM.FMS_VOUCHER_UPDATE);
  // 個人中心走 `/self`（`.self`）；財務管理走原路由（`.read`）
  const scope = mode === 'personal' ? FMS_READ_SCOPE.SELF : FMS_READ_SCOPE.ALL;
  const detailQuery = useQuery({
    queryKey: voucherDetailQueryKey(id, scope),
    queryFn: () => fetchVoucherDetail(id, { scope }),
    enabled: Boolean(id),
  });

  useEffect(() => {
    if (!detailQuery.isError) {
      return;
    }
    toast.error(getVoucherApiErrorMessage(detailQuery.error, '載入憑證失敗'));
  }, [detailQuery.isError, detailQuery.error]);

  const detail = detailQuery.data?.data;

  return (
    <PageShell variant="detail">
      <div className="layout-shell__body">
        <div className="mx-auto w-full max-w-4xl space-y-4">
          <div
            className={cn(
              'sticky top-0 z-20 -mx-1 flex flex-wrap items-center gap-2',
              'border-b border-slate-200 bg-canvas px-1 pb-3 pt-1',
            )}
          >
            <h1 className="page_title mb-0!">
              <span className="border-l-5 border-primary-400 pl-2.5">憑證詳情</span>
            </h1>
            <Button type="button" variant="outline" asChild>
              <Link to={BACK_TO[mode]}>返回列表</Link>
            </Button>
          </div>

          {detailQuery.isLoading ? (
            <p className="text-sm text-slate-500">載入中…</p>
          ) : null}

          {detailQuery.isError ? (
            <p className="text-sm text-destructive">
              {getVoucherApiErrorMessage(detailQuery.error, '載入憑證失敗')}
            </p>
          ) : null}

          {detail ? (
            <>
              <VoucherMainInfoCard
                detail={detail}
                actions={(
                  <VoucherCommandBar
                    key={`${detail.id}-${detail.record?.updatedAt ?? ''}`}
                    detail={detail}
                    mode={mode}
                  />
                )}
              />
              <DocumentAttachmentsCard
                businessId={String(detail.id)}
                ownerKey="fmsVoucher"
                api={voucherAttachmentsApi}
                policy={ATTACHMENT_POLICY_STANDARD}
                readOnly={!canUpdate || !canManageVoucherAttachments(detail.status)}
              />
            </>
          ) : null}
        </div>
      </div>
    </PageShell>
  );
};

VoucherDetailPage.propTypes = {
  mode: PropTypes.oneOf(['personal', 'finance']),
};

export default VoucherDetailPage;
